import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  getReprocessStatus, queueCallsForReprocess, startReprocessing, 
  stopReprocessing, hardStopReprocessing, clearReprocessQueue, 
  getReprocessStats, getCalls, getUsers
} from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  RefreshCw, Play, Square, AlertTriangle, CheckCircle, Clock, 
  Loader2, Search, Filter, Zap, BarChart3, Users, Phone,
  StopCircle, XCircle, Trash2, Info
} from 'lucide-react';

export default function ReprocessCalls() {
  const { tenant, user } = useOutletContext();
  const tenantId = tenant?.id || tenant?._id;
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Status state
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Calls selection state
  const [calls, setCalls] = useState([]);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);

  // Reprocess options
  const [reprocessTranscript, setReprocessTranscript] = useState(true);
  const [reprocessAnalysis, setReprocessAnalysis] = useState(true);

  // Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await getReprocessStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching reprocess status:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await getReprocessStats(tenantId);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching reprocess stats:', error);
    }
  }, [tenantId]);

  // Fetch calls
  const fetchCalls = useCallback(async () => {
    try {
      setLoadingCalls(true);
      const params = { limit: 500 };
      if (userFilter && userFilter !== 'all') {
        params.user_id = userFilter;
      }
      const response = await getCalls(params);
      // API returns { calls: [...], total: ... } - extract calls array
      const callsData = response.data?.calls || response.data || [];
      // Filter to only completed calls
      const completedCalls = callsData.filter(c => c.processing_status === 'completed');
      setCalls(completedCalls);
    } catch (error) {
      console.error('Error fetching calls:', error);
      toast.error('Failed to load calls');
    } finally {
      setLoadingCalls(false);
    }
  }, [userFilter]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchStats(), fetchCalls(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchStats, fetchCalls, fetchUsers]);

  // Poll status when processing
  useEffect(() => {
    if (status?.is_processing) {
      const interval = setInterval(fetchStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [status?.is_processing, fetchStatus]);

  // Queue calls for reprocessing
  const handleQueueCalls = async (callIds, allCalls = false) => {
    try {
      const data = {
        tenant_id: tenantId || null,
        call_ids: callIds || [],
        all_calls: allCalls,
        reprocess_transcript: reprocessTranscript,
        reprocess_analysis: reprocessAnalysis
      };
      
      const requestedBy = user?.id || 'system';
      console.log('Queueing calls:', { data, requestedBy, tenantId, user });
      
      const response = await queueCallsForReprocess(tenantId, data, requestedBy);
      console.log('Queue response:', response);
      
      if (response.data?.success) {
        toast.success(response.data?.message || 'Calls queued for reprocessing');
        fetchStatus();
        fetchStats();
        setSelectedCalls([]);
        setShowConfirmDialog(false);
      } else {
        toast.error(response.data?.message || 'No calls found to queue');
      }
    } catch (error) {
      console.error('Error queuing calls:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to queue calls for reprocessing';
      toast.error(errorMsg);
    }
  };

  // Start processing
  const handleStart = async () => {
    try {
      const response = await startReprocessing(tenantId, user.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error starting reprocessing:', error);
      toast.error('Failed to start reprocessing');
    }
  };

  // Stop processing
  const handleStop = async () => {
    try {
      await stopReprocessing();
      toast.success('Stop signal sent');
      fetchStatus();
    } catch (error) {
      console.error('Error stopping:', error);
      toast.error('Failed to stop');
    }
  };

  // Hard stop
  const handleHardStop = async () => {
    try {
      await hardStopReprocessing();
      toast.success('Hard stop initiated');
      fetchStatus();
    } catch (error) {
      console.error('Error hard stopping:', error);
      toast.error('Failed to hard stop');
    }
  };

  // Clear queue
  const handleClearQueue = async () => {
    try {
      await clearReprocessQueue(tenantId);
      toast.success('Queue cleared');
      fetchStatus();
      fetchStats();
    } catch (error) {
      console.error('Error clearing queue:', error);
      toast.error('Failed to clear queue');
    }
    setShowConfirmDialog(false);
  };

  // Confirm action
  const handleConfirmAction = async () => {
    if (confirmAction === 'clear') {
      handleClearQueue();
    } else if (confirmAction === 'all') {
      await handleQueueCalls([], true);
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  // Filter calls
  const filteredCalls = calls.filter(call => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch = 
        call.filename?.toLowerCase().includes(search) ||
        call.candidate_name?.toLowerCase().includes(search) ||
        call.phone_number?.toLowerCase().includes(search) ||
        call.user?.name?.toLowerCase().includes(search) ||
        call.id?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    return true;
  });

  // Toggle call selection
  const toggleCallSelection = (callId) => {
    setSelectedCalls(prev => 
      prev.includes(callId) 
        ? prev.filter(id => id !== callId)
        : [...prev, callId]
    );
  };

  // Select all filtered calls
  const selectAllFiltered = () => {
    const allIds = filteredCalls.map(c => c.id);
    setSelectedCalls(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedCalls([]);
  };

  const queue = status?.queue || {};
  const progress = status?.progress || {};
  const progressPercent = progress.total > 0 
    ? Math.round(((progress.completed + progress.failed) / progress.total) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Re-Process Calls
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Re-analyze calls with improved speaker identification and stricter AI feedback
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchStatus(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Alert about strict scoring */}
      <Alert className={isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}>
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertDescription className={isDark ? 'text-amber-200' : 'text-amber-800'}>
          <strong>Strict Quality Standards Applied:</strong> Re-processed calls will be scored with ultra-strict standards. 
          Scores will focus on 4 key parameters: Energy, Clarity, Knowledge, and Quality of Discussion. 
          Only truly exceptional calls will receive high scores.
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className={`h-8 w-8 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                  {stats?.total_calls || 0}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className={`h-8 w-8 ${isDark ? 'text-green-400' : 'text-green-500'}`} />
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                  {stats?.reprocessed_calls || 0}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Reprocessed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className={`h-8 w-8 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                  {queue.pending || 0}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>In Queue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className={`h-8 w-8 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                  {stats?.pending_reprocess || 0}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Not Reprocessed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Status */}
      {(status?.is_processing || queue.pending > 0) && (
        <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              {status?.is_processing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  Processing...
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-yellow-500" />
                  Queue Ready
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {status?.is_processing && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    Current: {status.current_call || 'Starting...'}
                  </span>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {progress.completed}/{progress.total} ({progressPercent}%)
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-green-500 border-green-500">
                    Completed: {progress.completed}
                  </Badge>
                  <Badge variant="outline" className="text-red-500 border-red-500">
                    Failed: {progress.failed}
                  </Badge>
                </div>
              </>
            )}

            <div className="flex items-center gap-2">
              {!status?.is_processing && queue.pending > 0 && (
                <Button onClick={handleStart} data-testid="start-reprocess-btn">
                  <Play className="h-4 w-4 mr-2" />
                  Start Processing ({queue.pending} calls)
                </Button>
              )}
              
              {status?.is_processing && (
                <>
                  <Button variant="outline" onClick={handleStop}>
                    <StopCircle className="h-4 w-4 mr-2" />
                    Stop (Graceful)
                  </Button>
                  <Button variant="destructive" onClick={handleHardStop}>
                    <XCircle className="h-4 w-4 mr-2" />
                    Hard Stop
                  </Button>
                </>
              )}

              {!status?.is_processing && queue.pending > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => { setConfirmAction('clear'); setShowConfirmDialog(true); }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Queue
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reprocess Options */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : ''}>Reprocess Options</CardTitle>
          <CardDescription>Select what to reprocess for the calls</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className={isDark ? 'text-white' : ''}>Re-identify Speakers</Label>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Apply improved speaker detection algorithm to transcripts
              </p>
            </div>
            <Switch 
              checked={reprocessTranscript} 
              onCheckedChange={setReprocessTranscript} 
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className={isDark ? 'text-white' : ''}>Re-analyze with Strict Scoring</Label>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Regenerate AI feedback with ultra-strict quality standards
              </p>
            </div>
            <Switch 
              checked={reprocessAnalysis} 
              onCheckedChange={setReprocessAnalysis} 
            />
          </div>
        </CardContent>
      </Card>

      {/* Call Selection */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={isDark ? 'text-white' : ''}>Select Calls to Reprocess</CardTitle>
              <CardDescription>
                Choose individual calls, multiple calls, or all calls
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setConfirmAction('all'); setShowConfirmDialog(true); }}
                data-testid="reprocess-all-btn"
              >
                <Zap className="h-4 w-4 mr-2" />
                Reprocess ALL Calls
              </Button>
              {selectedCalls.length > 0 && (
                <Button 
                  size="sm"
                  onClick={() => handleQueueCalls(selectedCalls)}
                  data-testid="reprocess-selected-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess Selected ({selectedCalls.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search calls by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600' : ''}`}
                />
              </div>
            </div>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className={`w-[200px] ${isDark ? 'bg-gray-700 border-gray-600' : ''}`}>
                <SelectValue placeholder="Filter by user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={selectAllFiltered}>
              Select All ({filteredCalls.length})
            </Button>
            {selectedCalls.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                Clear
              </Button>
            )}
          </div>

          {/* Calls List */}
          {loadingCalls ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {filteredCalls.map(call => {
                  const isSelected = selectedCalls.includes(call.id);
                  const isReprocessed = call.reprocessed_with_strict_scoring;
                  
                  return (
                    <div 
                      key={call.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-500/10' 
                          : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleCallSelection(call.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox checked={isSelected} />
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : ''}`}>
                              {call.filename || call.id}
                            </p>
                            <div className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              <span>{call.user?.name || call.candidate_name || 'Unknown'}</span>
                              <span>•</span>
                              <span>Score: {call.average_score?.toFixed(1) || 0}</span>
                              <span>•</span>
                              <span>{call.call_type || 'Unknown'}</span>
                              {call.phone_number && (
                                <>
                                  <span>•</span>
                                  <span className="font-mono text-xs">{call.phone_number}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isReprocessed ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Reprocessed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className={isDark ? 'border-gray-600' : ''}>
                              Original
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {confirmAction === 'clear' ? 'Clear Queue?' : 'Reprocess ALL Calls?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'clear' 
                ? 'This will remove all pending calls from the reprocess queue.'
                : `This will queue ALL ${stats?.total_calls || 0} calls for reprocessing with strict scoring. This may take a while.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant={confirmAction === 'clear' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
            >
              {confirmAction === 'clear' ? 'Clear Queue' : 'Reprocess All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
